import React, { Fragment, useState } from "react";
import { Chip, Paper, Typography } from "@mui/material";
import { Check } from "@mui/icons-material";
import { BtmRequestTable } from "../btm-request-table/btm-request-table";
import { t } from "i18next";
import { BtmReturnRequestListItem } from "../model/btm-return-request-list-item";
import { BtmReturnRequestStatus } from "../model/btm-return-request-status";
import { BtmReturnRequestType } from "../model/btm-return-request-type";
import { BtmReturnRequestCategory } from "../model/btm-return-request-category";
import { BtmReturnsListSearch } from "./btm-returns-list-search";

interface IBtmReturnsListProps {
    data: BtmReturnRequestListItem[] | undefined;
    withStatus?: boolean;
    withActionColumn?: boolean;
    withFilters?: boolean;
    onRequestOpen: (request: BtmReturnRequestListItem) => void;
    isLoading: boolean;
}

export const BtmReturnsList = (props: IBtmReturnsListProps) => {
    const [statusFilterValue, setStatusFilterValue] = useState<BtmReturnRequestStatus | undefined>(undefined);
    const [typeFilterValue, setTypeFilterValue] = useState<BtmReturnRequestType | undefined>(undefined);
    const [categoryFilterValue, setCategoryFilterValue] = useState<BtmReturnRequestCategory | undefined>(undefined);
    const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

    const StatusFilter = ({ status }: { status: BtmReturnRequestStatus }) => {
        return <Chip color={status === statusFilterValue ? "primary" : "default"} variant={"outlined"}
                     label={t(`components.blueBrandReturns.status.${status}`)}
                     icon={status === statusFilterValue ? <Check /> : undefined}
                     onClick={() => setStatusFilterValue(previous =>
                         previous === status ? undefined : status)} />;
    };

    const TypeFilter = ({ type }: { type: BtmReturnRequestType }) => {
        return <Chip color={type === typeFilterValue ? "primary" : "default"} variant={"outlined"}
                     label={t(`components.blueBrandReturns.type.${type}`)}
                     icon={type === typeFilterValue ? <Check /> : undefined}
                     onClick={() => setTypeFilterValue(previous =>
                         previous === type ? undefined : type)} />;
    };

    const CategoryFilter = ({ category }: { category: BtmReturnRequestCategory }) => {
        return <Chip color={category === categoryFilterValue ? "primary" : "default"} variant={"outlined"}
                     label={t(`components.blueBrandReturns.category.${category}`)}
                     icon={category === categoryFilterValue ? <Check /> : undefined}
                     onClick={() => setCategoryFilterValue(previous =>
                         previous === category ? undefined : category)} />;
    };

    return <Fragment>
        <Paper elevation={0} className={"filters"}>
            <div className={"filter-group"}>
                <BtmReturnsListSearch value={globalFilterValue} onChange={setGlobalFilterValue} />
            </div>
            {props.withFilters && <Fragment>
                <div className={"filter-group"}>
                    <Typography color={"textSecondary"} className={"filter-group-title"}>
                        {t("components.blueBrandReturns.list.filter.status")}
                    </Typography>
                    <div className={"filter-group-contents"}>
                        <StatusFilter status={BtmReturnRequestStatus.New} />
                        <StatusFilter status={BtmReturnRequestStatus.Pending} />
                        <StatusFilter status={BtmReturnRequestStatus.Processed} />
                    </div>
                </div>
                <div className={"filter-group"}>
                    <Typography color={"textSecondary"} className={"filter-group-title"}>
                        {t("components.blueBrandReturns.list.filter.type")}
                    </Typography>
                    <div className={"filter-group-contents"}>
                        <TypeFilter type={BtmReturnRequestType.PhysicalReturn} />
                        <TypeFilter type={BtmReturnRequestType.AnticipatedRefundOnExpectedPhysicalReturn} />
                        <TypeFilter type={BtmReturnRequestType.VirtualReturn} />
                    </div>
                </div>
                <div className={"filter-group"}>
                    <Typography color={"textSecondary"} className={"filter-group-title"}>
                        {t("components.blueBrandReturns.list.filter.category")}
                    </Typography>
                    <div className={"filter-group-contents"}>
                        <CategoryFilter category={BtmReturnRequestCategory.Voluntary} />
                        <CategoryFilter category={BtmReturnRequestCategory.Involuntary} />
                    </div>
                </div>
            </Fragment>}
        </Paper>
        <BtmRequestTable isLoading={props.isLoading}
                         data={props.data}
                         withStatus={props.withStatus ?? false}
                         withActionColumn={props.withActionColumn ?? false}
                         onBtmRequestOpen={props.onRequestOpen}
                         columnFilters={{
                             quickSearch: globalFilterValue,
                             status: statusFilterValue,
                             type: typeFilterValue,
                             category: categoryFilterValue }} />
    </Fragment>;
};
